/* Discover v2 (2026-09-22). Behavior for the discover-* sections, loaded with defer.
   Every section renders the same script tag, so this file guards itself and runs once.
   - Sticky chip nav: sits under Empire's sticky header (measured, kept current with ResizeObserver).
   - This week: the three shelf cards become real tab buttons; the club card dates are computed here
     because the full-page cache makes Liquid's `now` stale.
   - Add buttons go through window.HeatCart one request at a time (never parallel cart calls). */
(function () {
  'use strict';
  if (window.__dv2Loaded) return;
  window.__dv2Loaded = true;

  var d = document;
  var reduceMotion = function () {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  /* ---------- Header offset for the sticky chip nav and anchor landings ---------- */
  function initHeaderOffset() {
    var header = d.querySelector('.site-header') || d.querySelector('[data-site-header]');
    var root = d.documentElement;
    var last = -1, queued = false;
    function measure() {
      queued = false;
      var h = 0;
      if (header) {
        var pos = window.getComputedStyle(header).position;
        if (pos === 'sticky' || pos === 'fixed') h = Math.round(header.getBoundingClientRect().height);
      }
      if (h !== last) {
        last = h;
        root.style.setProperty('--dv2-header-h', h + 'px');
      }
    }
    // Empire makes the header sticky from its own script, after this one may have run, and the
    // desktop header shrinks on scroll; re-measure on load, resize, header size and scroll.
    function queue() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame ? window.requestAnimationFrame(measure) : setTimeout(measure, 16);
    }
    measure();
    if (header && 'ResizeObserver' in window) new ResizeObserver(queue).observe(header);
    window.addEventListener('resize', queue, { passive: true });
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('load', measure);
    setTimeout(measure, 1500);
  }

  /* ---------- Chip nav: hide chips whose section is not on the page ---------- */
  // A section hidden in the theme editor ("disabled") is not rendered at all, so its anchor is
  // missing; its chip must not point at nothing. Re-checked when the editor adds or removes a section.
  function pruneChips() {
    Array.prototype.forEach.call(d.querySelectorAll('[data-dv2-jump] a[href^="#"]'), function (c) {
      var id = decodeURIComponent(c.getAttribute('href').slice(1));
      var li = c.parentNode;
      li.hidden = !(id && d.getElementById(id));
    });
    Array.prototype.forEach.call(d.querySelectorAll('[data-dv2-jump]'), function (nav) {
      var on = nav.querySelector('li:not([hidden]) a.is-on');
      if (!on) {
        var first = nav.querySelector('li:not([hidden]) a');
        Array.prototype.forEach.call(nav.querySelectorAll('a.is-on'), function (a) { a.classList.remove('is-on'); });
        if (first) first.classList.add('is-on');
      }
    });
  }

  /* ---------- Chip nav: highlight the section in view ---------- */
  function initChipNav(nav) {
    if (nav.getAttribute('data-dv2-ready')) return;
    nav.setAttribute('data-dv2-ready', '1');
    var list = nav.querySelector('.dv2-jump__list');
    var chips = Array.prototype.slice.call(nav.querySelectorAll('a[href^="#"]'));
    if (!chips.length || !('IntersectionObserver' in window)) return;
    function setOn(id) {
      chips.forEach(function (c) {
        var on = c.getAttribute('href') === '#' + id;
        c.classList.toggle('is-on', on);
        if (on) {
          c.setAttribute('aria-current', 'true');
          // keep the active chip visible in the horizontal row without moving the page
          var cl = c.offsetLeft, cr = cl + c.offsetWidth;
          if (cl < list.scrollLeft || cr > list.scrollLeft + list.clientWidth) {
            list.scrollTo({ left: Math.max(0, cl - 16), behavior: reduceMotion() ? 'auto' : 'smooth' });
          }
        } else {
          c.removeAttribute('aria-current');
        }
      });
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) setOn(e.target.id); });
    }, { rootMargin: '-45% 0px -50% 0px' });
    chips.forEach(function (c) {
      var id = decodeURIComponent(c.getAttribute('href').slice(1));
      var target = id && d.getElementById(id);
      if (target) io.observe(target);
    });
  }

  /* ---------- This week: tabs ---------- */
  function initTabs(tw) {
    var tablist = tw.querySelector('[data-dv2-tablist]');
    if (!tablist || tablist.getAttribute('data-dv2-ready')) return;
    tablist.setAttribute('data-dv2-ready', '1');
    var links = Array.prototype.slice.call(tablist.querySelectorAll('a[data-dv2-tab]'));
    if (!links.length) return;
    tablist.setAttribute('role', 'tablist');
    tablist.setAttribute('aria-label', tablist.getAttribute('data-label') || 'Shelves');
    var tabs = links.map(function (a) {
      var b = d.createElement('button');
      b.type = 'button';
      b.className = a.className;
      b.innerHTML = a.innerHTML;
      b.id = a.id;
      b.setAttribute('role', 'tab');
      b.setAttribute('data-dv2-tab', a.getAttribute('data-dv2-tab'));
      b.setAttribute('aria-controls', a.getAttribute('data-panel'));
      var li = a.parentNode;
      li.setAttribute('role', 'presentation');
      li.replaceChild(b, a);
      var panel = d.getElementById(a.getAttribute('data-panel'));
      if (panel) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', b.id);
      }
      return b;
    });
    function select(tab, focus) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        var p = d.getElementById(t.getAttribute('aria-controls'));
        if (p) p.hidden = !on;
      });
      if (focus) tab.focus();
      // compact variant: panel color and the See-all link follow the chosen tab
      var key = tab.getAttribute('data-dv2-tab');
      tw.setAttribute('data-active', key);
      Array.prototype.forEach.call(tw.querySelectorAll('[data-dv2-for]'), function (el) {
        el.hidden = el.getAttribute('data-dv2-for') !== key;
      });
      var sync = tw.__dv2Sync;
      if (sync) window.requestAnimationFrame(sync);
    }
    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { select(t, false); });
      t.addEventListener('keydown', function (e) {
        var k = e.key, n = null;
        if (k === 'ArrowRight' || k === 'ArrowDown') n = tabs[(i + 1) % tabs.length];
        else if (k === 'ArrowLeft' || k === 'ArrowUp') n = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (k === 'Home') n = tabs[0];
        else if (k === 'End') n = tabs[tabs.length - 1];
        if (n) { e.preventDefault(); select(n, true); }
      });
    });
    var want = null;
    try { want = new URLSearchParams(window.location.search).get('tab'); } catch (e) {}
    var first = tabs[0];
    tabs.forEach(function (t) { if (want && t.getAttribute('data-dv2-tab') === want) first = t; });
    select(first, false);
  }

  /* ---------- This week: dates (ship date leads; the cutoff day is only ever "order by") ---------- */
  function initDates(tw) {
    if (!window.Intl || !Intl.DateTimeFormat) return;
    var cutoff = parseInt(tw.getAttribute('data-club-cutoff-day'), 10) || 23;
    var parts;
    try {
      parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: 'numeric', day: 'numeric' })
        .formatToParts(new Date()).reduce(function (o, p) { o[p.type] = p.value; return o; }, {});
    } catch (e) { return; }
    var y = +parts.year, mo = +parts.month, day = +parts.day;
    if (!y || !mo || !day) return;
    var DAY = 864e5;
    var today = new Date(Date.UTC(y, mo - 1, day));
    var fmt = function (x, o) { var opts = { timeZone: 'UTC' }; for (var k in o) opts[k] = o[k]; return x.toLocaleDateString('en-US', opts); };
    var set = function (sel, t) { Array.prototype.forEach.call(tw.querySelectorAll(sel), function (el) { el.textContent = t; }); };
    var monday = new Date(today.getTime() - ((today.getUTCDay() + 6) % 7) * DAY);
    set('[data-tw-week]', fmt(monday, { month: 'long', day: 'numeric', year: 'numeric' }));
    Array.prototype.forEach.call(tw.querySelectorAll('[data-tw-week]'), function (el) {
      el.setAttribute('datetime', monday.toISOString().slice(0, 10));
    });
    var m = (mo - 1) + (day <= cutoff ? 1 : 2);
    var ship = new Date(Date.UTC(y, m, 1)), orderBy = new Date(Date.UTC(y, m - 1, cutoff));
    var days = Math.round((ship - today) / DAY);
    set('[data-tw-ship-mon]', fmt(ship, { month: 'short' }).toUpperCase());
    set('[data-tw-ship-day]', String(ship.getUTCDate()));
    set('[data-tw-ship-dow]', fmt(ship, { weekday: 'long' }));
    set('[data-tw-ship-long]', fmt(ship, { weekday: 'long', month: 'long', day: 'numeric' }));
    set('[data-tw-ship-short]', fmt(ship, { weekday: 'short', month: 'short', day: 'numeric' }));
    var ob = fmt(orderBy, { weekday: 'short', month: 'short', day: 'numeric' });
    var gap = Math.round((orderBy - today) / DAY);
    var obShort = ob.replace(/^\w+, /, '');
    set('[data-tw-order-by]', gap === 0 ? 'today, ' + obShort : gap === 1 ? 'tomorrow, ' + obShort : ob);
    set('[data-tw-days]', String(days));
    set('[data-tw-days-unit]', days === 1 ? 'day' : 'days');
  }

  /* ---------- This week: shelf arrows (the shelf scrolls and snaps without them) ---------- */
  function initShelves(tw) {
    var syncs = [];
    Array.prototype.forEach.call(tw.querySelectorAll('.dv2-shelf'), function (s) {
      var list = s.querySelector('.dv2-shelf__list');
      var btns = s.querySelectorAll('.dv2-shelf__btn');
      if (!list || btns.length < 2) return;
      function sync() {
        btns[0].disabled = list.scrollLeft < 4;
        btns[1].disabled = list.scrollLeft + list.clientWidth > list.scrollWidth - 4;
      }
      Array.prototype.forEach.call(btns, function (b) {
        b.addEventListener('click', function () {
          list.scrollBy({ left: +b.getAttribute('data-dir') * list.clientWidth * 0.85, behavior: reduceMotion() ? 'auto' : 'smooth' });
        });
      });
      list.addEventListener('scroll', sync, { passive: true });
      syncs.push(sync);
      sync();
    });
    // compact variant: one pair of arrows in the tab row drives whichever shelf is showing
    var tools = tw.querySelector('[data-dv2-tools]');
    if (tools) {
      var tb = tools.querySelectorAll('.dv2-shelf__btn');
      var current = function () {
        var s = tw.querySelector('.dv2-shelf:not([hidden]) .dv2-shelf__list');
        return s;
      };
      var toolSync = function () {
        var l = current();
        if (!l || tb.length < 2) return;
        tb[0].disabled = l.scrollLeft < 4;
        tb[1].disabled = l.scrollLeft + l.clientWidth > l.scrollWidth - 4;
      };
      Array.prototype.forEach.call(tb, function (b) {
        b.addEventListener('click', function () {
          var l = current();
          if (l) l.scrollBy({ left: +b.getAttribute('data-dir') * l.clientWidth * 0.85, behavior: reduceMotion() ? 'auto' : 'smooth' });
        });
      });
      Array.prototype.forEach.call(tw.querySelectorAll('.dv2-shelf__list'), function (l) {
        l.addEventListener('scroll', toolSync, { passive: true });
      });
      syncs.push(toolSync);
      toolSync();
    }
    var all = function () { syncs.forEach(function (f) { f(); }); };
    tw.__dv2Sync = all;
    window.addEventListener('resize', all, { passive: true });
  }

  function initThisWeek(tw) {
    if (tw.getAttribute('data-dv2-ready')) return;
    tw.setAttribute('data-dv2-ready', '1');
    tw.classList.add('dv2-js');
    initShelves(tw);
    initTabs(tw);
    initDates(tw);
  }

  /* ---------- Flavor lanes: ?lanes=food deep link ---------- */
  function initLanes(fl) {
    if (fl.getAttribute('data-dv2-ready')) return;
    fl.setAttribute('data-dv2-ready', '1');
    try {
      if (new URLSearchParams(window.location.search).get('lanes') === 'food') {
        var r = fl.querySelector('.dv2-fl__radio--food');
        if (r) r.checked = true;
      }
    } catch (e) {}
  }

  /* ---------- Add to cart: one request at a time through HeatCart ---------- */
  var queue = Promise.resolve();
  function refreshCartCount() {
    if (!window.HeatCart || !window.HeatCart.getCart) return Promise.resolve();
    return window.HeatCart.getCart().then(function (cart) {
      if (!cart) return;
      d.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      d.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
    }).catch(function () {});
  }
  function onAdd(btn) {
    if (btn.getAttribute('data-state')) return;
    var id = Number(btn.getAttribute('data-variant-id'));
    if (!id || !window.HeatCart) {
      var href = btn.getAttribute('data-url');
      if (href) window.location.href = href;
      return;
    }
    var label = btn.textContent;
    btn.setAttribute('data-state', 'busy');
    btn.setAttribute('aria-busy', 'true');
    queue = queue.then(function () {
      return window.HeatCart.addToCart({ items: [{ id: id, quantity: 1 }] })
        .then(function (r) {
          if (!r.ok) {
            return r.json().catch(function () { return {}; }).then(function (j) {
              throw new Error((j && j.description) || 'Could not add');
            });
          }
          return r.json();
        })
        .then(function () {
          btn.setAttribute('data-state', 'added');
          btn.textContent = btn.getAttribute('data-added-label') || 'Added';
          return refreshCartCount();
        })
        .catch(function (err) {
          btn.removeAttribute('data-state');
          btn.textContent = 'Try again';
          btn.title = err && err.message ? err.message : '';
        })
        .then(function () {
          btn.removeAttribute('aria-busy');
          setTimeout(function () {
            if (btn.getAttribute('data-state') === 'added') btn.removeAttribute('data-state');
            btn.textContent = label;
          }, 1800);
        });
    });
  }
  d.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-dv2-add]');
    if (!btn) return;
    e.preventDefault();
    onAdd(btn);
  });

  /* ---------- Boot ---------- */
  function initAll(scope) {
    pruneChips();
    Array.prototype.forEach.call(scope.querySelectorAll('[data-dv2-jump]'), initChipNav);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-dv2-thisweek]'), initThisWeek);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-dv2-lanes]'), initLanes);
  }
  function boot() {
    initHeaderOffset();
    initAll(d);
  }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot);
  else boot();
  d.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  d.addEventListener('shopify:section:unload', function () { setTimeout(pruneChips, 0); });
})();
